import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { MediaMatcher } from '@angular/cdk/layout';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription, distinctUntilChanged, filter, map } from 'rxjs';
import { MenuItems, Menu, HorizontalMenuEntry } from '../../../shared/menu-items/menu-items';
import { AuthService } from '../../../auth/services/auth.service';

@Component({
  selector: 'app-horizontal-sidebar',
  templateUrl: './horizontal-sidebar.component.html',
  styleUrls: []
})
export class HorizontalAppSidebarComponent implements OnInit, OnDestroy {
  mobileQuery: MediaQueryList;

  /**
   * Grouped nav — rebuilt only when the role or privileges change, never per
   * change-detection cycle. See MenuItems.getHorizontalMenu() comment.
   */
  menu: HorizontalMenuEntry[] = [];

  /** Name of the group whose child route is currently active, or null. */
  activeGroup: string | null = null;

  /**
   * Groups whose dropdown should stay hidden even while :hover is true
   * — because the user just clicked a child link. Cleared when the
   * cursor actually leaves the group, so the next hover re-opens it.
   */
  justClicked = new Set<string>();

  private _mobileQueryListener: () => void;
  private _routerSub?: Subscription;
  private _authSub?: Subscription;

  constructor(
    changeDetectorRef: ChangeDetectorRef,
    media: MediaMatcher,
    public menuItems: MenuItems,
    private router: Router,
    private authService: AuthService
  ) {
    this.mobileQuery = media.matchMedia('(min-width: 1024px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);
  }

  ngOnInit(): void {
    // Privileges arrive after login from a separate request, so rebuild the
    // privilege-filtered menu when they land instead of only at startup.
    this._authSub = this.authService.authState$
      .pipe(
        map(state => `${state.user?.role ?? ''}|${(state.privileges || []).map(p => p.name).join(',')}`),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.menu = this.menuItems.getHorizontalMenu();
        this.updateActiveGroup(this.router.url);
      });

    this._routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.updateActiveGroup(e.urlAfterRedirects));
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeListener(this._mobileQueryListener);
    this._routerSub?.unsubscribe();
    this._authSub?.unsubscribe();
  }

  private updateActiveGroup(url: string): void {
    // Strip query/fragment, normalize to a plain path prefix check.
    const path = url.split('?')[0].split('#')[0];
    let active: string | null = null;

    for (const entry of this.menu) {
      if (entry.type !== 'group') { continue; }
      const hit = entry.children.some(c => path === '/' + c.state || path.startsWith('/' + c.state + '/'));
      if (hit) {
        active = entry.name;
        break;
      }
    }

    this.activeGroup = active;
  }

  /** Called when a dropdown child link is clicked. Forces the dropdown
   *  shut even though the cursor may still be over the group. */
  onChildClick(groupName: string): void {
    this.justClicked.add(groupName);
  }

  /** Called when the cursor leaves a group. Clears the "just clicked"
   *  flag so a subsequent hover opens the dropdown normally again. */
  onGroupLeave(groupName: string): void {
    this.justClicked.delete(groupName);
  }

  /** Used by the template to declare the type-narrow in a strict-template-friendly way. */
  asGroup(entry: HorizontalMenuEntry): { type: 'group'; name: string; icon: string; children: Menu[] } {
    return entry as { type: 'group'; name: string; icon: string; children: Menu[] };
  }

  /** Same for link entries. */
  asLink(entry: HorizontalMenuEntry): { type: 'link'; name: string; icon: string; state: string; exact: boolean } {
    return entry as { type: 'link'; name: string; icon: string; state: string; exact: boolean };
  }
}
